// Talking to the counting endpoints.
//
// Same rules as captureClient: every call is RELATIVE and same-origin, because
// the handset's cookie is host-only and SameSite=Strict and both quietly stop
// working the moment the API lives on another origin. Do not "simplify" any of
// these to an absolute backend URL.
//
// What is different from the capture side is what a failure means. A failed
// upload can be retried from a queue holding the bytes; a failed count has
// nothing held anywhere, because the number's home is Shopify. So nothing here
// retries on its own — the screen reports the failure and the person decides.
// A silent retry on a write that has no compare-and-set could set a stale
// number over a fresh one.

import { SessionExpired } from "./captureClient";
import { tracedFetch } from "./tracedFetch";

const API = "/api/capture/count";

async function get(path) {
  const res = await tracedFetch(path, { headers: { Accept: "application/json" } });
  if (res.status === 401) throw new SessionExpired();
  const text = await res.text();
  let parsed = null;
  try {
    parsed = JSON.parse(text);
  } catch {
    // Non-JSON here almost always means Caddy or Vite answered instead of the
    // backend. Say that rather than "unexpected token <".
    throw new Error(
      `${path} did not return JSON (status ${res.status}). Is /api proxied to the backend?`,
    );
  }
  if (!res.ok) {
    const err = new Error(parsed?.error || `request_failed_${res.status}`);
    err.code = parsed?.error;
    err.detail = parsed?.detail || null;
    throw err;
  }
  return parsed;
}

/** The shop's locations. Read live by the backend; there is no cache. */
export async function fetchLocations() {
  const data = await get(`${API}/locations`);
  return {
    locations: data.locations || [],
    // False when the shop has not granted `read_locations`. The screen has to
    // say so: choosing between unnamed locations is a guess, and a correct
    // count written to a guessed location has no error message.
    namesAvailable: data.namesAvailable !== false,
  };
}

/**
 * A page of this shop's barcodes.
 *
 * NO QUANTITY COMES BACK, deliberately. The list used to carry
 * `Variant.inventory`, which is the rollup across every location — a different
 * quantity from the one being counted, not merely a stale one. The popup reads
 * the real per-location figure when it opens.
 */
export async function fetchItems({ q = "", cursor = null, limit = 40 } = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (cursor) params.set("cursor", cursor);
  params.set("limit", String(limit));
  return get(`${API}/list?${params.toString()}`);
}

/** What Shopify holds for this barcode at this location, right now. */
export async function fetchItem({ barcode, locationId }) {
  const params = new URLSearchParams({ barcode, locationId });
  return get(`${API}/item?${params.toString()}`);
}

/**
 * Set the available quantity. This reaches the merchant's live inventory.
 *
 * NO RETRY, deliberately — see the header.
 */
export async function setCount({ barcode, locationId, quantity, create = false }) {
  const res = await tracedFetch(API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // `create` is sent only when the person has tapped a button that says a
    // product will be made. The backend treats anything but an explicit true as
    // "do not create".
    body: JSON.stringify({ barcode, locationId, quantity, create }),
  });
  if (res.status === 401) throw new SessionExpired();

  const text = await res.text();
  let parsed = null;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error(`count did not return JSON (status ${res.status}).`);
  }
  if (!res.ok) {
    const err = new Error(parsed?.error || `count_failed_${res.status}`);
    err.code = parsed?.error;
    err.detail = parsed?.detail || null;
    throw err;
  }
  return parsed;
}

/** The one place refusal codes become sentences a person on a shelf can act on. */
export function messageFor(err) {
  switch (err?.code) {
    case "barcode_not_found":
      return "We do not hold this barcode yet.";
    case "barcode_check_digit_failed":
      // The one refusal that is about the SCAN rather than the catalogue.
      return "That barcode does not check out — it is usually a misread. Scan it again.";
    case "no_inventory_item_after_create":
      return "The product was created but Shopify gave it nothing to stock. Check it in your admin.";
    case "no_inventory_item":
    case "not_countable":
      return "Shopify is not tracking stock for this product, so there is nothing to set.";
    case "shopify_refused":
      // Shopify's own words. Some items answer "not allowed to be adjusted via
      // API" with no bundle in sight, and diagnosing that here would be guessing.
      return err.detail
        ? `Shopify refused this: ${err.detail}`
        : "Shopify refused this change.";
    case "count_log_not_configured":
    case "count_not_recorded":
      // The count was NOT written. Say so plainly — this is the one refusal a
      // person might otherwise read as "probably saved anyway".
      return "Not saved: this count could not be recorded, so nothing was changed.";
    case "no_shopify_session":
      return "Cannot reach Shopify for this store right now.";
    case "quantity_invalid":
      return "Enter a whole number of items, zero or more.";
    case "location_required":
      return "Choose which location you are counting first.";
    default:
      return err?.message || "That did not work.";
  }
}
